/**
 * 直播Agent（RPA 坐席端）接口
 * 覆盖配置读写、账号开关、AI 扩写、触达记录与报表
 * 文档：docs/superpowers/work/rpa-frontend-api.md
 */
import { apiGet, apiPost } from '@/utils/request';
import type {
  CommentRecordParams,
  CommentTouchRecord,
  LikeFollowRecord,
  LikeFollowRecordParams,
  LiveAgentScene,
  LiveDmRecord,
  LiveDmRecordParams,
  LiveDmStats,
  LiveSessionsDashboard,
  LiveSessionsDashboardParams,
  ReachDashboard,
  ReachDashboardParams,
  RpaAiExpandResult,
  RpaChatRecordParams,
  RpaChatRecordResult,
  RpaPagedResult,
} from '@/types/liveAgent';

/** 配置接口：按 scene + account_id 读写 */
const configApi = {
  get: <T>(scene: LiveAgentScene, accountId: string) =>
    apiGet<T>('/rpa-agent/config', { scene, account_id: accountId }),

  save: <T>(scene: LiveAgentScene, accountId: string, data: unknown) =>
    apiPost<T>('/rpa-agent/config', { scene, account_id: accountId, data }),

  /** 复制当前账号配置到其他账号 */
  copy: (data: { scene: LiveAgentScene; account_id: string; target_account_ids: string[] }) =>
    apiPost<{ copied: number }>('/rpa-agent/copy', data),
};

/** AI 扩写：基于已有话术生成变体 */
const aiExpand = (contents: string[], count = 3) =>
  apiPost<RpaAiExpandResult>('/rpa-agent/ai-expand', { contents, count });

/** 触达记录 */
const recordsApi = {
  liveDmStats: (params: { account_id: string; start_date?: string; end_date?: string }) =>
    apiGet<LiveDmStats>('/rpa-agent/live-dm-stats', params),

  liveDm: (params: LiveDmRecordParams) =>
    apiGet<RpaPagedResult<LiveDmRecord>>('/rpa-agent/live-dm-records', {
      ...params,
    }),

  comment: (params: CommentRecordParams) =>
    apiGet<RpaPagedResult<CommentTouchRecord>>('/rpa-agent/comment-records', {
      ...params,
    }),

  likeFollow: (params: LikeFollowRecordParams) =>
    apiGet<RpaPagedResult<LikeFollowRecord>>('/rpa-agent/like-follow-records', {
      ...params,
    }),

  /** 查看触达用户的聊天记录，分页默认 50 / 最大 200 */
  chatRecord: (params: RpaChatRecordParams) =>
    apiGet<RpaChatRecordResult>('/rpa-agent/chat-record', { ...params }),
};

/** 报表 */
const statApi = {
  /** 触达数据统计（T+1） */
  dashboard: (data: ReachDashboardParams) => apiPost<ReachDashboard>('/rpa/stat/dashboard', data),

  /** 直播情况看板 */
  liveSessions: (data: LiveSessionsDashboardParams) =>
    apiPost<LiveSessionsDashboard>('/rpa/stat/live-sessions', data),
};

export const liveAgentApi = {
  ...configApi,
  aiExpand,
  ...recordsApi,
  ...statApi,
};
